import React, { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { useRequiredFields } from 'reusable/useRequiredFields';
import { OPERATOR_COMPANIES_PATH } from 'router/pathName';
import CompanyFields, { companyValues, trimmedCompany, useTimeZones } from './CompanyFields';
import CompanyManagers from './CompanyManagers';
import CompanySupport from 'components/Support/CompanySupport';
import { COMPANY_FIELDS, CompanyField, CompanyValues, getCompany, OperatorCompany, updateCompany } from './operatorApi';
import { ConfirmDialog, ErrorNotice, SuccessNotice, primaryButton, useOperatorErrors } from './OperatorUi';

/**
 * YAC-2026-009 PR5b。会社の詳細と編集（運営）。変えた項目だけを PATCH で送る。削除は無い（11/1 の後）。
 * 時間帯を変えるときは、会社の設定の画面と同じく、保存の前に確認を出す（日付の区切りが変わるため）。
 * 下に、その会社の管理者の一覧と操作（CompanyManagers）を置く。
 */
const CompanyDetail: React.FC = () => {
  const { t } = useTranslation('layout');
  const { id = '' } = useParams();
  const location = useLocation();
  const errorsOf = useOperatorErrors();
  const nameId = useId();
  const [company, setCompany] = useState<OperatorCompany | null>(null);
  const [values, setValues] = useState<CompanyValues | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null
  );
  const [confirmZone, setConfirmZone] = useState<Partial<CompanyValues> | null>(null);
  const { zones, failed: zonesFailed } = useTimeZones();
  const required = useRequiredFields({ name: nameId });
  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoadError(null);
    try {
      const loaded = await getCompany(id);
      if (!isCurrent()) return;
      setCompany(loaded);
      setValues(companyValues(loaded));
    } catch (failure) {
      if (!isCurrent()) return;
      console.error('Failed to fetch company', failure);
      setLoadError(failure);
    }
  }, [beginRequest, id]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (changes: Partial<CompanyValues>) => {
    setBusy(true);
    setErrors([]);
    try {
      const saved = await updateCompany(id, changes);
      setCompany(saved);
      setValues(companyValues(saved));
      setNotice(t('operator.company.saved'));
      setConfirmZone(null);
    } catch (failure) {
      console.error('Failed to update company', failure);
      setErrors(errorsOf(failure));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !company || !values) return;
    const next = trimmedCompany(values);
    if (!required.check({ name: next.name })) return;
    const before = companyValues(company);
    const changes: Partial<CompanyValues> = {};
    COMPANY_FIELDS.forEach((key) => {
      if (next[key] !== before[key]) changes[key] = next[key];
    });
    if (Object.keys(changes).length === 0) {
      setErrors([]);
      setNotice(t('operator.company.no-changes'));
      return;
    }
    if (changes.time_zone !== undefined) {
      setErrors([]);
      setConfirmZone(changes);
      return;
    }
    await save(changes);
  };

  if (loadError) {
    return (
      <section className="px-4 py-3">
        <FetchFailed
          error={loadError}
          onRetry={load}
          message={t('ui.detail-fetch-failed', { ns: 'common' })}
          notFoundMessage={t('operator.company.not-found')}
        />
        <Link to={OPERATOR_COMPANIES_PATH} className="text-primary/300 underline">
          {t('operator.company.back')}
        </Link>
      </section>
    );
  }
  if (!company || !values) {
    return (
      <div role="status" className="p-4 text-neutral/300">
        {t('ui.loading', { ns: 'common' })}
      </div>
    );
  }

  const setField = (key: CompanyField, value: string) =>
    setValues((current) => (current ? { ...current, [key]: value } : current));

  return (
    <section className="flex flex-col gap-4 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-title font-bold text-neutral/300">
          {t('operator.company.detail-title')}：{company.name}
        </h1>
        <Link to={OPERATOR_COMPANIES_PATH} className="text-primary/300 underline">
          {t('operator.company.back')}
        </Link>
      </div>
      <SuccessNotice message={notice} onClose={() => setNotice(null)} />
      <form onSubmit={submit} noValidate className="flex flex-col gap-4 rounded-md bg-white p-6">
        <h2 className="text-subtitle-1 font-bold text-neutral/300">{t('operator.company.info')}</h2>
        <CompanyFields
          values={values}
          onChange={setField}
          nameId={nameId}
          requiredFields={required}
          zones={zones}
          zonesFailed={zonesFailed}
          disabled={busy}
        />
        <ErrorNotice messages={confirmZone ? [] : errors} />
        <div className="flex justify-end">
          <button type="submit" className={primaryButton} disabled={busy}>
            {t('operator.company.save')}
          </button>
        </div>
      </form>
      {/* 管理者の操作の後も、会社の欄（編集中の値）は読み直さない。 */}
      <CompanyManagers companyId={company.id} />
      {/* YAC-2026-009 PR7b。この会社の管理者として入る・この会社のサポートの記録と「終わらせる」。 */}
      <CompanySupport companyId={company.id} companyName={company.name} />
      {confirmZone ? (
        <ConfirmDialog
          title={t('operator.company.time-zone-confirm-title')}
          body={
            <p>
              {t('operator.company.time-zone-confirm', {
                before: company.time_zone || '-',
                after: confirmZone.time_zone,
              })}
            </p>
          }
          confirmLabel={t('operator.company.save')}
          busy={busy}
          errors={errors}
          onClose={() => {
            setConfirmZone(null);
            setErrors([]);
          }}
          onConfirm={() => save(confirmZone)}
        />
      ) : null}
    </section>
  );
};

export default CompanyDetail;
